import { Info } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";

import Label from "common/components/ui/Label";

import type { HTMLAttributes, JSX } from "react";

const formFieldVariants = cva("group flex flex-col overflow-hidden", {
  variants: {
    size: {
      sm: "gap-1",
      md: "gap-1.5",
      lg: "gap-2",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

interface FormFieldProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof formFieldVariants> {
  label: string;
  htmlFor: string;
  extraLabelComponent?: JSX.Element;
  error?: string;
  hint?: string;
}

function FormField({
  children,
  className,
  size,
  label,
  htmlFor,
  extraLabelComponent = null,
  error = null,
  hint = null,
}: FormFieldProps) {
  let bottomComponent: JSX.Element;

  if (error) {
    bottomComponent = (
      <p
        className={clsx(
          size === "lg" ? "text-sm" : "text-xs",
          "ml-1 text-red-500",
        )}
      >
        {error}
      </p>
    );
  } else if (hint) {
    bottomComponent = (
      <div
        className={clsx(
          "ml-1 flex items-center text-black-500",
          size === "lg" ? "gap-1" : "gap-0.5",
        )}
      >
        <Info weight="fill" size={size === "lg" ? 20 : 16} />
        <p className={size === "lg" ? "text-sm" : "text-xs"}>{hint}</p>
      </div>
    );
  }
  return (
    <div
      className={formFieldVariants({ size, className })}
      data-error={!!error || undefined}
    >
      <div className="flex items-center gap-1">
        <Label size={size === "lg" ? "md" : "sm"} htmlFor={htmlFor}>
          {label}
        </Label>
        {extraLabelComponent}
      </div>
      {children}
      {bottomComponent}
    </div>
  );
}

FormField.displayName = "FormField";

export default FormField;
